import { useEffect, useMemo, useState } from 'react'

import AccordionItem from '@/components/ui/accordion-item'
import HeadingWithAnchor from '@/components/heading-with-anchor'
import RichText from '@/components/ui/rich-text'

import { slugify } from '@/utils/strings'
import { useLocation } from '@/utils/url'
import { TypeSectionAccordion } from '@/types/ctf'
import { useInspectorProps } from '@/utils/live-preview'

type AccordionProps = TypeSectionAccordion<'WITHOUT_UNRESOLVABLE_LINKS'>['fields'] & {
  entryId?: string
}

export default function Accordion({
  entryId,
  title,
  anchorId,
  description,
  items,
  allowMultipleToBeOpen,
  isOpenByDefault = false,
}: AccordionProps) {
  const pageInspectorProps = useInspectorProps({ entryId, restrictedTypes: ['pages'] })

  const { hash } = useLocation()

  const getItemWithCurrentHash = (hash: string) => {
    return (
      items?.findIndex((item) => {
        const itemId = slugify(item?.fields.anchorId || item?.fields.title)
        return itemId === hash
      }) ?? -1
    )
  }

  const defaultOpenIndices: number[] = useMemo(() => {
    const itemWithHash = getItemWithCurrentHash(hash)
    if (itemWithHash !== -1) {
      return [itemWithHash]
    } else if (isOpenByDefault && !hash) {
      return [0]
    } else {
      return []
    }
  }, [hash, isOpenByDefault])

  const [openIndices, setOpenIndices] = useState<number[]>(() => defaultOpenIndices)

  const handleToggle = (isOpen: boolean, index: number) => {
    const isAlreadyOpen = openIndices.includes(index)
    if ((isOpen && isAlreadyOpen) || (!isOpen && !isAlreadyOpen)) return
    if (allowMultipleToBeOpen) {
      if (isOpen) {
        setOpenIndices((prev) => [...prev, index])
      } else {
        setOpenIndices((prev) => prev.filter((i) => i !== index))
      }
    } else {
      setOpenIndices(isOpen ? [index] : [])
    }
  }

  useEffect(() => {
    const itemWithHash = getItemWithCurrentHash(hash)
    if (itemWithHash !== -1) {
      handleToggle(true, itemWithHash)
    }
  }, [hash])

  return (
    <div
      className="bw-section-accordion my-8 [.bw-section-accordion_&]:mt-0"
      {...pageInspectorProps({ fieldId: 'title' })}
    >
      {title && <HeadingWithAnchor type="h2" id={slugify(anchorId || title)} title={title} />}
      {description && (
        <div className="mt-4">
          <RichText body={description} />
        </div>
      )}
      {items?.map(
        (item, index) =>
          item && (
            <AccordionItem
              key={item.sys.id}
              {...item.fields}
              content={item.fields.content || ''}
              isOpen={openIndices.includes(index)}
              onToggle={(isOpen) => handleToggle(isOpen, index)}
              disableHashDetection
            />
          )
      )}
    </div>
  )
}
